import moveMailListRowsHelper from './helpers/moveMailListRowsHelper';
import showNotificationOnMove from './helpers/showNotificationOnMove';
import { updateCountForConversationMove } from './helpers/updateFolderCountForArchiveMailbox';
import datapoints from '../datapoints';
import isDeletedOrUnderDeletedItemsFolder from '../helpers/isDeletedOrUnderDeletedItemsFolder';
import shouldPerformRowRemovalInstantUpdate from '../helpers/shouldPerformRowRemovalInstantUpdate';
import { ActionDatapoint } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getApolloClient } from 'owa-apollo';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isFolderUnderArchiveRoot } from 'owa-mail-store';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import { ApplyConversationActionContext } from 'owa-mail-triage-action-utils';
import getEwsRequestDateString from 'owa-mail-triage-action-utils/lib/getEwsRequestDateString';
import { doesTableSupportUndo } from 'owa-mail-undo';
import * as undoActions from 'owa-mail-undo';
import {
    getApplyConversationActionContext,
    getRowIdsFromRowKeys,
    TableView,
    MailFolderTableQuery,
} from 'owa-mail-list-store';
import {
    MoveConversationDocument,
    MoveConversationMutation,
} from '../../graphql/__generated__/moveConversationMutation.interface';
import {
    UndoMoveConversationDocument,
    UndoMoveConversationMutation,
} from '../../graphql/__generated__/undoMoveConversationMutation.interface';

type MoveConversationResult = MoveConversationMutation['moveConversation'];
type UndoMoveConversationResult = UndoMoveConversationMutation['undoMoveConversation'];

/**
 * The action worker which handles moving conversations.
 * @param rowKeys - the row keys of the rows that, need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
export default createLegacyActionWithDatapoint(
    'moveConversations',
    datapoints.moveConversation
)(function moveConversations(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    actionSource: ActionSource
) {
    return moveMailListRowsHelper(
        rowKeys,
        destinationFolderId,
        originalTableView,
        moveConversationsInternal,
        actionSource
    );
});

/**
 * The move conversations internal method
 * @param rowKeys - the row keys of the rows that need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
function moveConversationsInternal(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    actionSource: ActionSource,
    moveDatapoint: ActionDatapoint
): Promise<void> {
    // If the current view is of a favorite category, the folder is msgfolderroot
    // In that case, do not pass in a sourceFolderId (same scenario occurs when moving in a search) and disable undo
    const sourceFolderId =
        (originalTableView.tableQuery as MailFolderTableQuery).scenarioType !== 'category'
            ? originalTableView.tableQuery.folderId
            : null;

    const conversationActionContexts = rowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, originalTableView.id)
    );
    const conversationIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableView.id);
    const allowUndo = doesTableSupportUndo(sourceFolderId, getMailboxInfo(originalTableView));

    /* If folder is under archive mailbox update folder count manually in local, since archive mbx doesn't have
    hierarchy notification unread count won't get updated instantly */
    isFolderUnderArchiveRoot(destinationFolderId) &&
        updateCountForConversationMove(rowKeys, originalTableView, destinationFolderId);

    if (
        shouldPerformRowRemovalInstantUpdate(
            originalTableView.tableQuery,
            isDeletedOrUnderDeletedItemsFolder(destinationFolderId)
        )
    ) {
        deleteConversationStoreUpdate(conversationIdStrings, originalTableView, 'Move');
    }

    // Make service request
    const moveConversationPromise = exportedHelperFunctions.invokeMoveConversationMutation(
        conversationActionContexts,
        destinationFolderId,
        sourceFolderId
    );

    if (allowUndo) {
        undoActions.addActionToUndoStack(
            async () => {
                const response = await moveConversationPromise;
                if (response.success) {
                    await exportedHelperFunctions.invokeUndoMoveConversationMutation(
                        conversationActionContexts,
                        sourceFolderId,
                        destinationFolderId,
                        response.undoRequestId
                    );
                }
                return Promise.resolve();
            },
            originalTableView.serverFolderId,
            moveConversationPromise
        );
    }

    // Show notification bar immediately after client action
    showNotificationOnMove(conversationActionContexts.length, destinationFolderId, allowUndo);

    moveDatapoint.markUserPerceivedTime();

    return processApplyConversationActionResponseMessage(
        moveConversationPromise,
        rowKeys,
        sourceFolderId,
        originalTableView,
        true /* shouldRemoveRowsFromTombstone */,
        true /* skipCheckingResponseMessage */
    );
}

/**
 * Invoke the MoveConversation graphql mutation
 * @param conversationContexts the conversation contexts to be moved
 * @param destinationFolderId - the folder they need to move to
 * @param currentFolderId - the folder they originate in -- null for search scenarios
 */
const invokeMoveConversationMutationFn = async function invokeMoveConversationMutation(
    conversationContexts: ApplyConversationActionContext[],
    destinationFolderId: string,
    currentFolderId: string
): Promise<MoveConversationResult> {
    const conversationSnapShots = conversationContexts.map(context => {
        return {
            id: context.conversationId,
            messagesAsOf: getEwsRequestDateString(context.conversationLastSyncTimeStamp),
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: currentFolderId,
            destinationFolderId: destinationFolderId,
        },
        mutation: MoveConversationDocument,
    });
    return result?.data?.moveConversation;
};

/**
 * Invoke the UndoMoveConversation graphql mutation
 * @param conversationContexts the conversation contexts to be undoMoved
 * @param destinationFolderId - the folder they need to undoMove to
 * @param currentFolderId - the folder they originate in -- null for search scenarios
 */
const invokeUndoMoveConversationMutationFn = async function invokeUndoMoveConversationMutation(
    conversationContexts: ApplyConversationActionContext[],
    destinationFolderId: string,
    currentFolderId: string,
    undoRequestId: number | null | undefined
): Promise<UndoMoveConversationResult> {
    const conversationSnapShots = conversationContexts.map(context => {
        return {
            id: context.conversationId,
            messagesAsOf: getEwsRequestDateString(context.conversationLastSyncTimeStamp),
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: currentFolderId,
            destinationFolderId: destinationFolderId,
            undoRequestId: undoRequestId,
        },
        mutation: UndoMoveConversationDocument,
    });
    return result?.data?.undoMoveConversation;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeMoveConversationMutation: invokeMoveConversationMutationFn,
    invokeUndoMoveConversationMutation: invokeUndoMoveConversationMutationFn,
};
